import { useMany, useOne, useTranslate } from '@refinedev/core';
import type { HttpError } from '@refinedev/core';
import { Edit, useForm } from '@refinedev/antd';
import { Alert, Form } from 'antd';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import type {
  ContractUpdateIn,
  OrganizationOut,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ContractFormFields } from '@/components/tenancy/ContractFormFields';
import {
  buildPublisherTree,
  toPublisherTreeData,
  PUBLISHER_TREE_LIMIT,
} from '@/components/user_catalog/publishers/publisherTree';
import {
  toContractFormValues,
  toContractPayload,
} from '@/components/tenancy/contractFormValues';
import type { ContractFormValues } from '@/components/tenancy/contractFormValues';
import { useOrganizationPublisherLinks } from '@/hooks/tenancy/useOrganizationPublisherLinks';
import { isContractEditable } from './constants';
import type { ContractOut } from './constants';

export const ContractEdit = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps, query } = useForm<
    ContractOut,
    HttpError,
    ContractUpdateIn
  >({
    resource: 'tenancy.contracts',
    mutationMeta: { method: 'patch' },
  });

  const record = query?.data?.data;
  // Contracts already in effect are immutable.
  const editable = record ? isContractEditable(record) : true;

  const { result: organization } = useOne<OrganizationOut>({
    resource: 'tenancy.organizations',
    id: record?.organization_id ?? 0,
    queryOptions: { enabled: record?.organization_id != null },
  });
  // The backend checks membership on the contract's start.
  const { publisherIds } = useOrganizationPublisherLinks(
    record?.organization_id
  );
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    // The tree offers only what comes back, and `getMany` would otherwise
    // fall back to the server's default of 100.
    meta: { params: { limit: PUBLISHER_TREE_LIMIT } },
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherTreeData = toPublisherTreeData(
    buildPublisherTree(publishersResult?.data ?? [])
  );

  return (
    <Edit
      saveButtonProps={{
        ...saveButtonProps,
        disabled: !editable || saveButtonProps.disabled,
      }}
    >
      {!editable && (
        <Alert
          type="warning"
          showIcon
          message={t('tenancy.contracts.form.not_editable')}
          style={{ marginBottom: 16 }}
        />
      )}
      {record && (
        <Form
          {...formProps}
          layout="vertical"
          initialValues={toContractFormValues(record)}
          disabled={!editable}
          onFinish={(values) =>
            // antd types onFinish after the mutation payload, but the raw
            // form values (Dayjs valid_from) arrive here.
            formProps.onFinish?.(
              toContractPayload(values as unknown as ContractFormValues)
            )
          }
        >
          {/* organization is immutable after creation (no field on the
              PATCH schema) */}
          <Form.Item label={t('tenancy.contracts.fields.organization')}>
            <ShowIconLink
              resource="tenancy.organizations"
              id={record.organization_id}
            >
              {organization?.name ?? record.organization_id}
            </ShowIconLink>
          </Form.Item>

          <ContractFormFields publisherTreeData={publisherTreeData} />
        </Form>
      )}
    </Edit>
  );
};
